import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { parseToRgb } from 'polished';
import colorString from 'color-string';
import { RefObject } from 'react';
import { Canvas } from '@/lib/rust_canvas_app';
import { debounce } from 'lodash';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function parseColor(
  color: string,
  overrideAlpha?: number,
  useOverrideAlpha?: boolean
): {
  hex: string;
  fullHex: string;
  r: number;
  g: number;
  b: number;
  a?: number;
} | null {
  let parsed;
  try {
    parsed = parseToRgb(color);
  } catch {
    if (!color.startsWith('#')) {
      try {
        parsed = parseToRgb('#' + color);
      } catch {}
    }
  }
  if (!parsed) {
    return null;
  }
  const hex = colorString.to.hex([parsed.red, parsed.green, parsed.blue]);

  let alpha = 'alpha' in parsed ? (parsed.alpha as number) : undefined;

  if (overrideAlpha !== undefined && (useOverrideAlpha || alpha === undefined)) {
    alpha = overrideAlpha;
  }
  let fullHex = colorString.to.hex([parsed.red, parsed.green, parsed.blue, alpha ?? 1]);
  if (alpha === 1 && fullHex.length === 7) {
    fullHex += 'FF';
  }
  return {
    hex: hex,
    fullHex: fullHex,
    r: parsed.red,
    g: parsed.green,
    b: parsed.blue,
    a: alpha,
  };
}

export function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }

    h /= 6;
  }

  return [h * 360, s * 100, l * 100];
}

export function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  h /= 360;
  s /= 100;
  l /= 100;

  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

export function rgbaToString(r: number, g: number, b: number, a: number): string {
  const result = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  return `${result}${Math.round(a * 255)
    .toString(16)
    .padStart(2, '0')}`.toUpperCase();
}

export function hslToString(h: number, s: number, l: number, a: number): string {
  return `hsla(${Math.round(h)}, ${Math.round(s)}%, ${Math.round(l)}%, ${a})`;
}

export function rgb2hsv(r: number, g: number, b: number): { h: number; s: number; v: number } {
  const rr = r / 255;
  const gg = g / 255;
  const bb = b / 255;

  const cmax = Math.max(rr, gg, bb);
  const cmin = Math.min(rr, gg, bb);
  const delta = cmax - cmin;

  let h = 0;
  let s = 0;
  const v = cmax;

  if (delta !== 0) {
    if (cmax === rr) {
      h = 60 * (((gg - bb) / delta) % 6);
    } else if (cmax === gg) {
      h = 60 * ((bb - rr) / delta + 2);
    } else {
      h = 60 * ((rr - gg) / delta + 4);
    }
  }

  // Saturation
  if (cmax !== 0) {
    s = delta / cmax;
  }

  // Fix negative hue
  if (h < 0) {
    h += 360;
  }

  return { h, s, v };
}

export function copyShapesToLocalStorage(localStorage: Storage, shapes: string[]) {
  const data = {
    action: 'copy',
    shapes: shapes,
    timestamp: Date.now(),
  };
  localStorage.setItem('__canvas-clipboard', JSON.stringify(data));
}

export function getShapesFromLocalStorage(localStorage: Storage): string[] | null {
  const storedData = localStorage.getItem('__canvas-clipboard');
  if (storedData) {
    const data = JSON.parse(storedData);
    if (data.action === 'copy' && data.timestamp > Date.now() - 1000 * 60 * 60 * 24) {
      console.log('paste', data);
      const shapes = data.shapes;
      return shapes;
    }
  }
  return null;
}

export const recordUpdateShapeDebounced = debounce((canvasInstance: RefObject<Canvas | null>) => {
  canvasInstance.current?.record_update_shape();
}, 150);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const updateShapes = (
  canvasInstance: RefObject<Canvas | null>,
  shapes: string[],
  props: any
) => {
  canvasInstance.current?.update_shapes(shapes, props);
  recordUpdateShapeDebounced(canvasInstance);
};
